import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Order, OrderService, NayaxCaptureStatus } from '../api/api_pb';
import { useClient } from '../connect_api';
import CurrencyFormatter from '../utils/currencyFormatter';
import NayaxCaptureActions from '../components/NayaxCaptureActions';
import { formatCountdown, timestampToEpochSeconds } from '../utils/nayaxUtils';
import { Loader2, AlertTriangle } from 'lucide-react';

const AUTO_REFRESH_INTERVAL = 60000; // 60 seconds

function getUrgencyClass(capturableUntilSeconds: number): string {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const diffSeconds = capturableUntilSeconds - nowSeconds;

    if (diffSeconds <= 0) return 'bg-red-100 text-red-800';         // Expired
    if (diffSeconds < 3600) return 'bg-red-100 text-red-800';       // < 1 hour
    if (diffSeconds < 14400) return 'bg-yellow-100 text-yellow-800'; // < 4 hours
    return '';
}

const PendingFinalisations: React.FC = () => {
    const navigate = useNavigate();
    const orderClient = useClient(OrderService);

    const [orders, setOrders] = useState<Order[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const fetchOrders = useCallback(async () => {
        try {
            const response = await orderClient.listOrders({
                limit: 1000,
                page: 0,
                nayaxCaptureStatuses: [NayaxCaptureStatus.PENDING],
                sortBy: 'capturable_until',
                machineUuids: [],
                last4Digits: '',
                productUuids: [],
                mdbItemNumbers: [],
            });
            setOrders(response.orders);
        } catch (err) {
            console.error('Failed to fetch pending finalisations:', err);
        } finally {
            setIsLoading(false);
        }
    }, [orderClient]);

    useEffect(() => {
        fetchOrders();
        const interval = setInterval(fetchOrders, AUTO_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchOrders]);

    if (isLoading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
                <AlertTriangle className="w-6 h-6 text-orange-500" />
                <h1 className="text-2xl font-bold text-gray-800">Pending Finalisations</h1>
                <span className="text-sm text-gray-500">
                    {orders.length} order{orders.length !== 1 ? 's' : ''} awaiting action
                </span>
            </div>

            {orders.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <p className="text-gray-500 text-lg">No pending finalisations. All orders are up to date.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Machine</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pre-auth Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Remaining</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {orders.map((order, index) => {
                                const capturableUntilSeconds = timestampToEpochSeconds(order.capturableUntil);
                                const urgencyClass = getUrgencyClass(capturableUntilSeconds);
                                const countdown = capturableUntilSeconds > 0 ? formatCountdown(capturableUntilSeconds) : '';

                                return (
                                    <tr key={order.uuid} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <button
                                                onClick={() => navigate(`/show-lease/${order.customer?.uuid}`)}
                                                className="text-indigo-600 hover:text-indigo-900 font-mono text-sm"
                                            >
                                                {order.uuid}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                            {order.customer?.mobileNumber || '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                            {order.productName || '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                            {order.machineName || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-700">
                                            {order.paymentInformation ? (
                                                <div className="space-y-0.5">
                                                    {order.paymentInformation.rrn && (
                                                        <p className="text-xs"><span className="text-gray-400">RRN:</span> {order.paymentInformation.rrn}</p>
                                                    )}
                                                    {order.paymentInformation.paymentDeviceSessionId && (
                                                        <p className="text-xs"><span className="text-gray-400">Session:</span> {order.paymentInformation.paymentDeviceSessionId}</p>
                                                    )}
                                                    {order.paymentInformation.paymentTransactionId && (
                                                        <p className="text-xs"><span className="text-gray-400">Txn:</span> {order.paymentInformation.paymentTransactionId}</p>
                                                    )}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                            {order.paymentInformation ? (
                                                <CurrencyFormatter priceInCents={Number(order.paymentInformation.preauthAmountCents)} />
                                            ) : '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span
                                                data-testid={`time-remaining-${index}`}
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${urgencyClass}`}
                                            >
                                                {countdown}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <NayaxCaptureActions
                                                order={order}
                                                onActionComplete={fetchOrders}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PendingFinalisations;
